import axios, { AxiosInstance } from "axios";
import { BASE_URL } from "configs/api";
import { IDApp, ProjectAddFormValues, TDAppsResonse, TEcosystem, TTag } from "./types";
import { dAppsStore } from "./store";
import { $api } from "services";
import { notify } from "hooks/useToast/notify";
import { ICryptoProject } from "types/cryptoProject";

class DAppsApi {
  private _api: AxiosInstance;

  constructor() {
    this._api = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
    });
  }

  private handleError(error: any) {
    if (error.response) {
      notify({
        type: 'error',
        meassage: error.response.data.message,
      });
    } else {
      notify({
        type: 'error',
        meassage: 'Something went wrong',
      });
    }
  }

  async getDApps(page: number = 0, take: number = 10, refetch?: boolean): Promise<TDAppsResonse> {
    const url = `/api/dapps_info/admin/all`;
    try {
      const { data } = await $api.get<TDAppsResonse>(url, {
        params: { page, limit: take },
      });

      if (refetch) {
        const isThereMore = data.meta.totalPages !== page;

        dAppsStore.setState({
          dApps: data.data,
          hasMore: isThereMore,
          page: isThereMore ? page + 1 : page,
        })
      }

      return data;
    } catch (error) {
      this.handleError(error);
      return {
        data: [],
        meta: {
          itemsPerPage: 0,
          totalItems: 0,
          currentPage: 0,
          totalPages: 0,
        },
      }
    }
  }

  async getTags(): Promise<TTag[]> {
    const url = `/api/dapps_info/admin/tags/all`;
    const { setAvailableTags } = dAppsStore.getState();
    try {
      const { data } = await this._api.get<TTag[]>(url);
      // const data = mockCategories;
      setAvailableTags(data);

      return data;
    } catch (error) {
      this.handleError(error);
      return [];
    }
  }

  async getEcosystems(): Promise<TEcosystem[]> {
    const url = `/api/dapps_info/admin/ecosystems/all`;
    const { setAvailableEcocystems } = dAppsStore.getState();
    try {
      const { data } = await this._api.get<TEcosystem[]>(url);
      setAvailableEcocystems(data);

      return data;
    } catch (error) {
      this.handleError(error);
      return [];
    }
  }

  async createDApp(projectData: ProjectAddFormValues, callback?: () => void) {
    const url = `/api/dapps_info/add`;
    dAppsStore.setState({ isAddLoading: true })
    const { setDApps, dApps } = dAppsStore.getState()

    try {
      const payload = {
        name: projectData.projectName,
        description: projectData.projectDescription,
        image: projectData.image,
        tag_ids: projectData.tags.map((tag) => tag.id),
        ecosystems_ids: projectData.ecosystems.map((ecosystem) => ecosystem.id),
        website: projectData.websiteLink,
        twitter_url: projectData.twitterLink,
        discord_url: projectData.discordLink,
      };

      const { data } = await this._api.post<IDApp>(url, payload);

      if (!!callback) {
        callback()
      }

      setDApps([...dApps, data])

      notify({
        type: 'success',
        meassage: 'dApp has been successfully created',
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      dAppsStore.setState({ isAddLoading: false })
    }
  }

  async editDApp(projectData: ProjectAddFormValues, callback?: () => void) {
    const url = `/api/dapps_info/edit`;
    dAppsStore.setState({ isAddLoading: true })
    const { setDApps, dApps, availableTags } = dAppsStore.getState()

    try {
      const payload = {
        id: projectData.id,
        name: projectData.projectName,
        description: projectData.projectDescription,
        image: projectData.image,
        tag_ids: projectData.tags.map((tag) => tag.id),
        ecosystems_ids: projectData.ecosystems.map((ecosystem) => ecosystem.id),
        website: projectData.websiteLink,
        twitter_url: projectData.twitterLink,
        discord_url: projectData.discordLink,
      };

      await this._api.patch<IDApp>(url, payload);

      const prevDapp = dApps.find((dApp) => dApp.id === projectData.id);
      if (prevDapp) {
        const updatedDApp: IDApp = {
          name: projectData.projectName,
          description: projectData.projectDescription,
          image: projectData.image,
          id: projectData.id,
          website: projectData.websiteLink,
          twitter_url: projectData.twitterLink,
          discord_url: projectData.discordLink,
          tags: availableTags.filter((tag) => projectData.tags.some((t) => t.id === tag.id)),
          ecosystems: projectData.ecosystems,
        }

        const updatedDApps = dApps.map((dApp) => {
          if (dApp.id === projectData.id) {
            return updatedDApp;
          }

          return dApp;
        })

        setDApps(updatedDApps);
      }

      if (!!callback) {
        callback()
      }

      notify({
        type: 'success',
        meassage: 'dApp has been successfully updated',
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      dAppsStore.setState({ isAddLoading: false })
    }
  }

  async deleteDApp({ dAppId }: { dAppId: string }) {
    const { dApps, setDApps } = dAppsStore.getState();
    const url = `/api/dapps_info/${dAppId}`;

    try {
      await this._api.delete(url);

      const updatedDApps = dApps.filter((dApp) => dApp.id !== dAppId);
      setDApps(updatedDApps);

      notify({
        type: 'success',
        meassage: 'dApp has been successfully deleted',
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async getCryptoProjectInfo(twitterName: string) {
    const url = `/api/extension/crypto-projects-list?page=${1}&pageSize=10&searchQuery=${twitterName}`;
    const { setIsCryptoProjectLoading } = dAppsStore.getState();
    setIsCryptoProjectLoading(true);

    try {
      const { data } = await $api.get<{ projects: ICryptoProject[]; }>(url);
      const cryptoProject = data.projects[0];

      if (cryptoProject) {
        return cryptoProject;
      }

      return null;
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsCryptoProjectLoading(false);
    }
  }
}

export const dAppsAPI = new DAppsApi();
